import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/Badges";
import { PageHeader, Empty, Loader } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import CareMatchAlerts from "@/components/carematch/CareMatchAlerts";
import { NotificationSettings } from "@/components/carematch/NotificationSettings";
import { api, errMsg, fmtDate } from "@/lib/api";

function ProfileCard() {
  const { refresh } = useAuth();
  const { data } = useQuery({ queryKey: ["consumer-me"], queryFn: () => api.get("/consumer/me").then((r) => r.data) });
  const [f, setF] = useState({ phone: "", zip: "" });
  useEffect(() => { if (data) setF({ phone: data.phone || "", zip: data.zip || "" }); }, [data]);
  const save = async () => {
    try { await api.put("/consumer/me", { ...f, preferred_contact: "email" }); toast.success("Profile saved"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="cz-card p-6 space-y-4" data-testid="consumer-profile-card">
      <div className="font-display font-bold">Contact preferences</div>
      <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="consumer-phone-input" /></div>
      <div className="space-y-1.5"><Label>Home ZIP</Label><Input value={f.zip} maxLength={5} onChange={(e) => setF({ ...f, zip: e.target.value })} data-testid="consumer-zip-input" /></div>
      <Button className="rounded-full bg-[#003B2D]" onClick={save} data-testid="consumer-profile-save-btn">Save</Button>
      <p className="text-xs text-slate-500">Zunvel only shares your contact details with providers you send a care request to.</p>
    </div>
  );
}

export default function ConsumerDashboard() {
  const { user } = useAuth();
  const searches = useQuery({ queryKey: ["my-searches"], queryFn: () => api.get("/care-search/mine").then((r) => r.data) });
  const reqs = useQuery({ queryKey: ["my-requests"], queryFn: () => api.get("/consumer/care-requests").then((r) => r.data) });
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="consumer-dashboard">
      <PageHeader eyebrow="My Zunvel" title={`Hello, ${user?.name?.split(" ")[0]}`} subtitle="Track your care searches and the requests you've sent to providers."
        actions={<Button asChild className="rounded-full zv-btn-gold" data-testid="consumer-new-search-btn"><Link to="/find-care"><Search className="h-4 w-4 mr-1" />New care search</Link></Button>} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <CareMatchAlerts />
          <div className="cz-card p-6">
            <div className="font-display font-bold mb-4">My care requests</div>
            {reqs.isLoading ? <Loader /> : reqs.data.length === 0 ? <p className="text-sm text-slate-500">No requests yet.</p> : (
              <div className="divide-y" data-testid="consumer-requests-list">{reqs.data.map((r) => (
                <div key={r.id} className="py-3 flex items-center justify-between gap-3">
                  <div><Link to={`/providers/${r.provider?.slug}`} className="font-semibold text-slate-900 hover:text-teal-700">{r.provider?.name}</Link><div className="text-xs text-slate-500">{r.care_type} · sent {fmtDate(r.created_at)}</div></div>
                  <StatusPill status={r.status} />
                </div>
              ))}</div>
            )}
          </div>
          <div className="cz-card p-6">
            <div className="font-display font-bold mb-4">Saved care searches</div>
            {searches.isLoading ? <Loader /> : searches.data.length === 0 ? <Empty title="No searches yet" action={<Button asChild className="rounded-full"><Link to="/find-care">Start a search</Link></Button>} /> : (
              <div className="divide-y" data-testid="consumer-searches-list">{searches.data.map((s) => (
                <Link key={s.id} to={`/find-care/results/${s.id}`} className="py-3 flex items-center justify-between hover:text-teal-700">
                  <span className="text-sm"><b>{s.match_count}</b> matches · ZIP {s.answers.zip} · {fmtDate(s.created_at)}</span><ArrowRight className="h-4 w-4" />
                </Link>
              ))}</div>
            )}
          </div>
        </div>
        <div className="space-y-6">
          <ProfileCard />
          <div className="cz-card p-6" data-testid="carematch-notification-card"><div className="font-display font-bold mb-4">CareMatch™ notifications</div><NotificationSettings /></div>
        </div>
      </div>
    </div>
  );
}
